import { normalizeMessagingUser, messagingPeerKey } from './messagingUserUtils';

function haystackForUser(user) {
  const normalized = normalizeMessagingUser(user);
  if (!normalized) return '';
  return `${normalized.displayName || ''} ${normalized.handle || ''}`.toLowerCase();
}

export function userMatchesQuery(user, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return haystackForUser(user).includes(q);
}

export function conversationMatchesQuery(conversation, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const preview = String(conversation?.lastMessagePreview || '').toLowerCase();
  return userMatchesQuery(conversation?.otherUser, q) || preview.includes(q);
}

export function matchSubtitleForResult(result) {
  if (result?.matchType === 'message' && result?.matchSnippet) {
    return `“${result.matchSnippet}”`;
  }
  if (result?.matchType === 'name' && result?.matchSnippet) {
    return result.lastMessagePreview || 'No messages yet';
  }
  return result?.lastMessagePreview || result?.matchSnippet || 'No messages yet';
}

/**
 * Client-side name/preview filter for loaded lists (1+ characters).
 */
export function filterPeopleByQuery({ query, conversations = [], followers = [], following = [], activeTab = 'all' }) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return null;

  const seen = new Set();
  const results = [];

  const pushUser = (user, conv = null) => {
    const normalized = normalizeMessagingUser(user);
    const key = messagingPeerKey(normalized);
    if (!key || seen.has(key)) return;
    if (!userMatchesQuery(user, q) && !conversationMatchesQuery(conv, q)) return;
    seen.add(key);
    results.push({ user, conversation: conv });
  };

  if (activeTab === 'all' || activeTab === 'followers' || activeTab === 'following') {
    for (const conv of conversations) {
      if (conversationMatchesQuery(conv, q)) {
        pushUser(conv.otherUser, conv);
      }
    }
  }

  if (activeTab === 'followers' || activeTab === 'all') {
    for (const user of followers) {
      pushUser(user, null);
    }
  }

  if (activeTab === 'following' || activeTab === 'all') {
    for (const user of following) {
      pushUser(user, null);
    }
  }

  return results;
}

/**
 * Merge conversation search, profile search, and client-side matches.
 */
export function mergeSearchResults(
  conversationResults = [],
  clientResults = [],
  profileResults = []
) {
  const byUserId = new Map();

  for (const hit of conversationResults) {
    const key = messagingPeerKey(hit?.otherUser);
    if (!key) continue;
    byUserId.set(key, {
      user: hit.otherUser,
      conversation: hit,
      matchType: hit.matchType,
      matchSnippet: hit.matchSnippet,
      fromApi: true,
    });
  }

  for (const profile of profileResults || []) {
    const normalized = normalizeMessagingUser(profile);
    const key = messagingPeerKey(normalized);
    if (!key || byUserId.has(key)) continue;
    byUserId.set(key, {
      user: normalized,
      conversation: null,
      matchType: 'name',
      matchSnippet: normalized?.handle || '',
      fromApi: true,
    });
  }

  for (const row of clientResults || []) {
    const key = messagingPeerKey(row.user);
    if (!key || byUserId.has(key)) continue;
    byUserId.set(key, {
      user: row.user,
      conversation: row.conversation,
      matchType: row.conversation ? 'preview' : 'name',
      matchSnippet: row.conversation?.lastMessagePreview || '',
      fromApi: false,
    });
  }

  return [...byUserId.values()];
}
